import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Loader2, Upload, Save, Image as ImageIcon, ShieldAlert, Eye,
    Users, ShieldCheck, Crown, MessageCircleQuestion, MessageSquare, Search,
    UserCircle, Trash2, Sparkles, RefreshCw, Check, X, ShoppingBag, Wand2, Star,
    Flag, Ban, AlertTriangle, Gift, Trophy,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { RoleBadge } from "@/components/RoleBadge";
import { FlowMeter } from "@/components/FlowMeter";
import { Activity, Flame } from "lucide-react";
import { BannerDisplay, RARITY_META } from "@/components/Banner";
import { GiveawayManager } from "@/components/GiveawayManager";

const SLOT_DEFS = [
    { key: "free_1", label: "Free Pick", aura: "#22c55e", tier: "FREE" },
    { key: "pro_1", label: "Pro Pick #1", aura: "#3b82f6", tier: "PRO" },
    { key: "pro_2", label: "Pro Pick #2", aura: "#a855f7", tier: "PRO" },
    { key: "premium_1", label: "Premium Pick #1", aura: "#f97316", tier: "SPORTS PREMIUM" },
    { key: "premium_2", label: "Premium Pick #2", aura: "#ef4444", tier: "SPORTS PREMIUM" },
    { key: "bonus_1", label: "Bonus Pick (Holo)", aura: "#facc15", tier: "PRO + PREMIUM" },
];

const RISKS = [
    { value: "low", label: "Low Risk" },
    { value: "medium", label: "Medium Risk" },
    { value: "high", label: "High Risk" },
    { value: "demon", label: "Demon Risk" },
];

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;
const TARGET_IMAGE_KB = 600; // target after client-side compression — stays well under
//                            ingress request-body limits (~1MB) on production.

// Compress + downscale an image client-side using a canvas. Returns a JPEG dataURL.
// Iteratively reduces quality until the resulting base64 fits under target_kb.
// If compression fails for ANY reason (HEIC, exotic format, broken decoder), falls
// back to raw base64 so the preview/save always works.
async function compressImageToDataUrl(file, targetKb = TARGET_IMAGE_KB, maxDim = 1600) {
    if (!file.type || !file.type.startsWith("image/")) {
        return await fileToBase64(file);
    }
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            const timeoutId = setTimeout(() => reject(new Error("decode-timeout")), 8000);
            i.onload = () => { clearTimeout(timeoutId); resolve(i); };
            i.onerror = () => { clearTimeout(timeoutId); reject(new Error("decode-failed")); };
            i.src = url;
        });
        let { width, height } = img;
        if (!width || !height) throw new Error("zero-dimensions");
        if (Math.max(width, height) > maxDim) {
            const scale = maxDim / Math.max(width, height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("no-canvas-ctx");
        ctx.drawImage(img, 0, 0, width, height);
        let quality = 0.92;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        let approxBytes = Math.round(dataUrl.length * 0.75);
        while (approxBytes > targetKb * 1024 && quality > 0.4) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL("image/jpeg", quality);
            approxBytes = Math.round(dataUrl.length * 0.75);
        }
        let scale = 1;
        while (approxBytes > targetKb * 1024 && scale > 0.5) {
            scale -= 0.15;
            const w2 = Math.round(width * scale);
            const h2 = Math.round(height * scale);
            canvas.width = w2;
            canvas.height = h2;
            ctx.drawImage(img, 0, 0, w2, h2);
            dataUrl = canvas.toDataURL("image/jpeg", 0.85);
            approxBytes = Math.round(dataUrl.length * 0.75);
        }
        if (!dataUrl || !dataUrl.startsWith("data:image/")) {
            throw new Error("invalid-dataurl");
        }
        return dataUrl;
    } catch (e) {
        // Fallback: read the raw file as base64 — preview always works even if browser
        // can't decode the format (HEIC, AVIF, etc.). Server-side it'll still be served.
        // eslint-disable-next-line no-console
        console.warn("compressImageToDataUrl fallback:", e?.message || e);
        try {
            return await fileToBase64(file);
        } catch (e2) {
            throw new Error("Could not read image file. Try saving it as JPG or PNG first.");
        }
    } finally {
        URL.revokeObjectURL(url);
    }
}

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

// ----------- Picks Tab -----------
function PicksManager() {
    const { user } = useAuth();
    const [slots, setSlots] = useState({});
    const [date, setDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const isAdmin = user?.role === "admin";
    const [savingSlot, setSavingSlot] = useState(null);

    const fetchAdminPicks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/picks/today");
            setDate(res.data.date);
            const incoming = res.data.slots || {};
            const next = {};
            for (const def of SLOT_DEFS) {
                next[def.key] = {
                    image_base64: incoming[def.key]?.image_base64 || "",
                    risk: incoming[def.key]?.risk || "low",
                    title: incoming[def.key]?.title || "",
                    description: incoming[def.key]?.description || "",
                };
            }
            setSlots(next);
        } catch (e) { toast.error("Failed to load picks"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchAdminPicks(); }, [fetchAdminPicks]);

    const onFileChange = async (slotKey, file) => {
        if (!file) return;
        if (file.size > MAX_IMAGE_BYTES) {
            toast.error(`Image too large (max ${(MAX_IMAGE_BYTES / 1024 / 1024).toFixed(0)}MB)`);
            return;
        }
        try {
            // Compress + downscale to ~600KB max so requests never get killed by ingress.
            // Falls back to raw base64 if compression fails (HEIC, AVIF, etc.).
            const b64 = await compressImageToDataUrl(file, TARGET_IMAGE_KB);
            if (!b64 || !b64.startsWith("data:")) {
                throw new Error("Image read returned empty result");
            }
            const sizeKb = Math.round((b64.length * 0.75) / 1024);
            setSlots((s) => ({ ...s, [slotKey]: { ...s[slotKey], image_base64: b64 } }));
            toast.success(`Image attached (${sizeKb} KB) \u2014 click Save to publish`);
        } catch (e) {
            // Surface the actual reason so the admin can fix the source file.
            const msg = e?.message || "Failed to read file";
            toast.error(`Upload failed: ${msg}. Tip: Save your image as JPG or PNG and try again.`);
        }
    };

    const updateField = (slotKey, field, value) => {
        setSlots((s) => ({ ...s, [slotKey]: { ...s[slotKey], [field]: value } }));
    };

    const clearImage = (slotKey) => {
        setSlots((s) => ({ ...s, [slotKey]: { ...s[slotKey], image_base64: "" } }));
    };

    // Per-slot save: each call is a small, separate request — so a single oversized
    // image can never silently kill the entire batch save like the all-in-one save did.
    const saveSlot = async (slotKey, announce = false) => {
        if (!isAdmin) { toast.error("Only Owner can publish picks"); return; }
        const slot = slots[slotKey];
        if (!slot) return;
        try {
            await api.post("/admin/picks/today/slot", {
                slot_key: slotKey,
                slot,
                date,
                announce,
            });
            return true;
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Save failed";
            toast.error(`${slotKey}: ${detail}`);
            return false;
        }
    };

    const saveAll = async () => {
        if (!isAdmin) {
            toast.error("Only Owner can publish picks");
            return;
        }
        setSaving(true);
        let okCount = 0;
        let failCount = 0;
        try {
            const slotKeys = SLOT_DEFS.map((d) => d.key);
            for (let i = 0; i < slotKeys.length; i += 1) {
                const isLast = i === slotKeys.length - 1;
                const ok = await saveSlot(slotKeys[i], isLast);
                if (ok) okCount += 1; else failCount += 1;
            }
            if (failCount === 0) {
                toast.success(`All ${okCount} picks saved \ud83d\udd25`);
            } else if (okCount > 0) {
                toast(`Saved ${okCount}/${slotKeys.length} picks \u2014 ${failCount} failed`);
            } else {
                toast.error("Save failed for all slots \u2014 check connection or image sizes");
            }
            await fetchAdminPicks();
        } finally { setSaving(false); }
    };

    const saveSingleSlot = async (slotKey) => {
        setSavingSlot(slotKey);
        try {
            const ok = await saveSlot(slotKey, true);
            if (ok) {
                toast.success(`${slotKey.toUpperCase()} saved \ud83d\udd25`);
                await fetchAdminPicks();
            }
        } finally { setSavingSlot(null); }
    };

    if (loading) return (
        <div className="grid gap-4">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-40 rounded-xl pp-bg-1" />)}
        </div>
    );

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
                <div>
                    <p className="text-sm text-[#8b8ba0]">Date: <span className="font-mono text-white">{date || "\u2014"}</span></p>
                    <p className="text-xs text-[#8b8ba0]">Set image, title, description, and risk for each card. Save when ready.</p>
                </div>
                {isAdmin ? (
                    <Button onClick={saveAll} disabled={saving} className="bg-[#22c55e] hover:brightness-110 text-[#07130b] h-11 px-5 font-semibold" data-testid="owner-save-button">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save All Picks</span>}
                    </Button>
                ) : (
                    <span className="text-xs font-mono uppercase text-[#22d3ee]">Read-only \u2014 Owner publishes picks</span>
                )}
            </div>
            <div className="grid gap-4">
                {SLOT_DEFS.map((def) => {
                    const s = slots[def.key] || { image_base64: "", risk: "low", title: "", description: "" };
                    return (
                        <Card key={def.key} className="pp-bg-1 border-[#242436] text-white" data-testid={`owner-slot-${def.key}`}>
                            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-1">
                                    <div className="relative pp-bg-0 rounded-xl border border-[#242436] overflow-hidden aspect-[4/3] flex items-center justify-center" style={{ boxShadow: `0 0 0 1px ${def.aura}33, 0 0 18px ${def.aura}33` }}>
                                        {s.image_base64 ? (
                                            <img src={s.image_base64} alt={`${def.label} preview`} className="absolute inset-0 w-full h-full object-contain" data-testid={`owner-slot-${def.key}-preview`} />
                                        ) : (
                                            <div className="text-center text-[#8b8ba0] text-sm">
                                                <ImageIcon className="w-8 h-8 mx-auto mb-1" /> No image yet
                                            </div>
                                        )}
                                    </div>
                                    {isAdmin && (
                                        <div className="mt-3 flex gap-2">
                                            <Label className="flex-1">
                                                <span className="sr-only">Upload {def.label}</span>
                                                <input type="file" accept="image/*" className="hidden" onChange={(e) => onFileChange(def.key, e.target.files?.[0])} data-testid={`owner-upload-input-${def.key}`} id={`upload-${def.key}`} />
                                                <Button asChild variant="secondary" className="w-full bg-[#0a0a0f] border border-[#242436] hover:bg-[#1b1b26] text-white">
                                                    <label htmlFor={`upload-${def.key}`} className="cursor-pointer inline-flex items-center justify-center gap-2 w-full h-10">
                                                        <Upload className="w-4 h-4" /> Upload
                                                    </label>
                                                </Button>
                                            </Label>
                                            {s.image_base64 && (
                                                <Button variant="secondary" onClick={() => clearImage(def.key)} className="bg-[#0a0a0f] border border-[#242436] hover:bg-[#1b1b26] text-white">Clear</Button>
                                            )}
                                        </div>
                                    )}
                                    {isAdmin && (
                                        <div className="mt-2 flex items-center justify-between gap-2">
                                            <span className="text-[10px] font-mono text-[#8b8ba0]">
                                                {s.image_base64
                                                    ? `\u{1F4E6} ${Math.round((s.image_base64.length * 0.75) / 1024)} KB`
                                                    : "no image"}
                                            </span>
                                            <Button
                                                onClick={() => saveSingleSlot(def.key)}
                                                disabled={savingSlot === def.key || saving}
                                                size="sm"
                                                className="bg-[#22c55e] hover:brightness-110 text-[#07130b] font-semibold h-8 text-[11px] px-3"
                                                data-testid={`owner-save-slot-${def.key}`}
                                                title="Save just this slot"
                                            >
                                                {savingSlot === def.key
                                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    : <span className="inline-flex items-center gap-1"><Save className="w-3 h-3" /> Save</span>}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                <div className="md:col-span-2 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-display text-xl tracking-widest" style={{ color: def.aura }}>{def.label}</p>
                                            <p className="text-xs font-mono uppercase text-[#8b8ba0]">{def.tier} TIER</p>
                                        </div>
                                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md" style={{ color: def.aura, border: `1px solid ${def.aura}55`, background: `${def.aura}11` }}>
                                            <Eye className="w-3.5 h-3.5" /> Preview key
                                        </span>
                                    </div>
                                    <div>
                                        <Label className="text-[#b7b7c6]">Title</Label>
                                        <Input disabled={!isAdmin} value={s.title} onChange={(e) => updateField(def.key, "title", e.target.value)} placeholder="e.g. NBA Goblin Special" className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid={`owner-title-${def.key}`} />
                                    </div>
                                    <div>
                                        <Label className="text-[#b7b7c6]">Description</Label>
                                        <Textarea disabled={!isAdmin} value={s.description} onChange={(e) => updateField(def.key, "description", e.target.value)} placeholder="Optional notes" rows={2} className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid={`owner-description-${def.key}`} />
                                    </div>
                                    <div>
                                        <Label className="text-[#b7b7c6] inline-flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> Risk</Label>
                                        <Select disabled={!isAdmin} value={s.risk} onValueChange={(v) => updateField(def.key, "risk", v)}>
                                            <SelectTrigger className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid={`owner-risk-select-${def.key}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="pp-bg-0 border-[#242436] text-white">
                                                {RISKS.map((r) => <SelectItem key={r.value} value={r.value} className="text-white">{r.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}

// ----------- Users Tab -----------
function UsersManager() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/users", { params: q ? { q } : {} });
            setItems(res.data.items || []);
        } catch (e) { toast.error("Failed to load users (admin only)"); }
        finally { setLoading(false); }
    }, [q]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const setRole = async (uid, role) => {
        setUpdating(uid);
        try {
            await api.post(`/admin/users/${uid}/role`, { role });
            toast.success(`Role updated to ${role.toUpperCase()}`);
            await fetchUsers();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update role");
        } finally { setUpdating(null); }
    };

    const grantTier = async (email, tier) => {
        const tierLabel = tier === "sports_premium" ? "Sports Premium" : tier === "pro" ? "Pro" : "Free";
        const confirmMsg = tier === "free"
            ? `Remove paid access from ${email}?`
            : `Grant ${tierLabel} (7 days) to ${email}? Use this only as a webhook-failure fallback.`;
        if (!window.confirm(confirmMsg)) return;
        setUpdating(email);
        try {
            await api.post("/dev/grant-tier", { email, tier });
            toast.success(`${email} is now ${tierLabel}`);
            await fetchUsers();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to grant tier");
        } finally { setUpdating(null); }
    };

    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8b8ba0]" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name" className="pl-9 pp-bg-0 border-[#242436] text-white" data-testid="users-search-input" />
                </div>
                <Button onClick={fetchUsers} variant="secondary" className="bg-[#14141c] border border-[#242436] text-white">Search</Button>
            </div>
            {loading ? (
                <div className="grid gap-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-14 rounded-md pp-bg-1" />)}</div>
            ) : items.length === 0 ? (
                <p className="text-sm text-[#8b8ba0] py-8 text-center">No users found.</p>
            ) : (
                <div className="space-y-2" data-testid="users-list">
                    {items.map((u) => (
                        <div key={u.id} className="pp-bg-1 border border-[#242436] rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid={`user-row-${u.id}`}>
                            <span className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-white" style={{ background: u.role === "admin" ? "#facc15" : u.role === "support" ? "#22d3ee" : "#a855f7" }}>
                                {(u.display_name || u.email)[0]?.toUpperCase()}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-semibold text-white truncate">{u.display_name || u.email}</p>
                                    <RoleBadge role={u.role} />
                                </div>
                                <p className="text-xs text-[#8b8ba0] truncate">{u.email} \u00b7 {u.tier}</p>
                            </div>
                            <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                                <SelectTrigger className="w-32 pp-bg-0 border-[#242436] text-white" data-testid={`user-role-select-${u.id}`}>
                                    {updating === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <SelectValue />}
                                </SelectTrigger>
                                <SelectContent className="pp-bg-0 border-[#242436] text-white">
                                    <SelectItem value="user" className="text-white">User</SelectItem>
                                    <SelectItem value="support" className="text-white">Support</SelectItem>
                                    <SelectItem value="admin" className="text-white">Admin</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={u.tier} onValueChange={(v) => grantTier(u.email, v)}>
                                <SelectTrigger className="w-36 pp-bg-0 border-[#242436] text-white" data-testid={`user-tier-select-${u.id}`}>
                                    {updating === u.email ? <Loader2 className="w-4 h-4 animate-spin" /> : <SelectValue />}
                                </SelectTrigger>
                                <SelectContent className="pp-bg-0 border-[#242436] text-white">
                                    <SelectItem value="free" className="text-white">Free</SelectItem>
                                    <SelectItem value="pro" className="text-white">Pro (7d)</SelectItem>
                                    <SelectItem value="sports_premium" className="text-white">Premium (7d)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ----------- Help Tab -----------
function HelpManager() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/help/requests");
            setItems(res.data.items || []);
        } catch (e) { toast.error("Failed to load help requests"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [load]);

    const claim = async (id) => {
        try {
            const res = await api.post(`/help/requests/${id}/claim`);
            toast.success("Claimed \u2014 opening DM");
            nav(`/messages/${res.data.conversation_id}`);
        } catch (e) { toast.error("Failed to claim"); }
    };
    const resolve = async (id) => {
        try { await api.post(`/help/requests/${id}/resolve`); await load(); }
        catch (e) { toast.error("Failed"); }
    };

    return (
        <div>
            {loading ? (
                <div className="grid gap-2">{[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-md pp-bg-1" />)}</div>
            ) : items.length === 0 ? (
                <p className="text-sm text-[#8b8ba0] py-8 text-center">No open help requests.</p>
            ) : (
                <div className="space-y-3" data-testid="help-requests-list">
                    {items.map((r) => (
                        <div key={r.id} className="pp-bg-1 border border-[#242436] rounded-md p-3 flex items-start gap-3" data-testid={`help-request-${r.id}`}>
                            <span className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-white" style={{ background: "#22d3ee" }}>
                                <MessageCircleQuestion className="w-4 h-4" />
                            </span>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-white">{r.user_display_name} <span className="text-xs text-[#8b8ba0] font-mono">{r.user_email}</span></p>
                                <p className="text-sm text-[#b7b7c6] mt-1 whitespace-pre-wrap">{r.message}</p>
                                <p className="text-xs text-[#8b8ba0] mt-1 font-mono">Status: {r.status}</p>
                            </div>
                            <div className="flex gap-2">
                                {r.status === "pending" ? (
                                    <Button onClick={() => claim(r.id)} className="bg-[#22d3ee] hover:brightness-110 text-[#062229] font-semibold" data-testid={`help-claim-${r.id}`}>
                                        <MessageSquare className="w-3.5 h-3.5 mr-1" /> Claim & DM
                                    </Button>
                                ) : (
                                    <Button onClick={() => nav(`/messages/${r.conversation_id}`)} variant="secondary" className="bg-[#14141c] border border-[#242436] text-white">Open DM</Button>
                                )}
                                <Button onClick={() => resolve(r.id)} variant="secondary" className="bg-[#14141c] border border-[#242436] text-white" title="Mark resolved"><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ----------- Flow Meter Tab -----------
function FlowMeterManager() {
    const [winrate, setWinrate] = useState(50);
    const [label, setLabel] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/flow-meter");
            setWinrate(res.data.winrate ?? 50);
            setLabel(res.data.label || "");
        } catch (e) { /* noop */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/flow-meter", { winrate: parseInt(winrate, 10), label: label.trim() || null });
            toast.success("Flow meter updated");
            await load();
        } catch (e) { toast.error("Failed to update"); }
        finally { setSaving(false); }
    };

    if (loading) return <Skeleton className="h-64 rounded-xl pp-bg-1" />;

    return (
        <div className="grid gap-6">
            <FlowMeter compact />
            <Card className="pp-bg-1 border-[#242436] text-white" data-testid="flow-meter-control">
                <CardContent className="p-6 space-y-5">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md" style={{ background: "#1a0f2e", border: "1px solid #a855f755" }}>
                            <Flame className="w-4 h-4 text-[#a855f7]" />
                        </span>
                        <div>
                            <p className="font-display text-xl tracking-widest">CONTROL THE FLOW</p>
                            <p className="text-xs text-[#8b8ba0]">Set today's winrate %. Updates the public Flow Meter on the home page in real-time.</p>
                        </div>
                    </div>
                    <div>
                        <Label className="text-[#b7b7c6] inline-flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Winrate %</Label>
                        <div className="mt-3 flex items-center gap-4">
                            <Slider value={[winrate]} onValueChange={(v) => setWinrate(v[0])} min={0} max={100} step={1} className="flex-1" data-testid="flow-meter-slider" />
                            <Input type="number" min={0} max={100} value={winrate} onChange={(e) => setWinrate(Math.max(0, Math.min(100, parseInt(e.target.value || 0, 10))))} className="w-20 pp-bg-0 border-[#242436] text-white text-center" data-testid="flow-meter-number" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[#b7b7c6]">Label (optional)</Label>
                        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. ON A HEATER 🔥" className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="flow-meter-label-input" maxLength={80} />
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={save} disabled={saving} className="bg-[#22c55e] hover:brightness-110 text-[#07130b] font-semibold" data-testid="flow-meter-save-button">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-2"><Save className="w-4 h-4" /> Update Flow</span>}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ----------- Banner Shop Manager Tab -----------
const ANIMATION_OPTIONS = [
    { value: "pulse_green", label: "Pulse Green" },
    { value: "thunder_blue", label: "Thunder Blue" },
    { value: "flames_red", label: "Flames Red" },
    { value: "lava_orange", label: "Lava Orange" },
    { value: "particles_gold", label: "Gold Particles" },
    { value: "holographic_rainbow", label: "Holographic Rainbow" },
];

const RARITY_OPTIONS = [
    { value: "common", label: "Common" },
    { value: "rare", label: "Rare" },
    { value: "epic", label: "Epic" },
    { value: "legendary", label: "Legendary" },
    { value: "mythic", label: "Mythic" },
];

function BannerShopManager() {
    const [banners, setBanners] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null); // banner id or "seed" or "draft"
    const [giftBanner, setGiftBanner] = useState(null); // banner being gifted
    const [giftEmail, setGiftEmail] = useState("");
    const [giftBusy, setGiftBusy] = useState(false);
    const [form, setForm] = useState({
        name: "",
        prompt: "",
        rarity: "common",
        price: 1.99,
        animation: "pulse_green",
        color: "#a855f7",
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [bRes, dRes] = await Promise.all([
                api.get("/shop/banners"),
                api.get("/admin/shop/banners/drafts"),
            ]);
            setBanners(bRes.data.items || []);
            setDrafts(dRes.data.items || []);
        } catch (e) {
            toast.error("Failed to load banner shop");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const regenerate = async (slug) => {
        setBusy(slug);
        try {
            await api.post(`/admin/shop/banners/${slug}/regenerate`);
            toast.success("Banner regenerated");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Regenerate failed");
        } finally { setBusy(null); }
    };

    const seedMissing = async () => {
        setBusy("seed");
        try {
            const res = await api.post("/admin/shop/banners/seed-images");
            toast.success(`Generated ${res.data.count || 0} missing image(s)`);
            await load();
        } catch (e) {
            toast.error("Seed images failed");
        } finally { setBusy(null); }
    };

    const removeBanner = async (id) => {
        if (!window.confirm("Remove this banner from the shop? Existing owners keep it.")) return;
        setBusy(id);
        try {
            await api.delete(`/admin/shop/banners/${id}`);
            toast.success("Banner removed");
            await load();
        } catch (e) {
            toast.error("Failed to remove");
        } finally { setBusy(null); }
    };

    const toggleActive = async (banner) => {
        const action = banner.active ? "Retire" : "Bring back to shop";
        if (banner.active && !window.confirm(`${action} "${banner.name}"? It will disappear from the public shop. Owners keep theirs forever.`)) return;
        setBusy(banner.id);
        try {
            await api.post(`/admin/shop/banners/${banner.id}/toggle-active`);
            toast.success(banner.active ? `${banner.name} retired` : `${banner.name} is live again`);
            await load();
        } catch (e) {
            toast.error("Failed to toggle");
        } finally { setBusy(null); }
    };

    const weeklyRefresh = async () => {
        if (!window.confirm("END THE WEEK?\n\nThis retires ALL currently-live banners in one click. Users who already bought them KEEP them in their vault. After this, use the AI generator below to drop 6 new banners for the new week.\n\nProceed?")) return;
        setBusy("weekly");
        try {
            const res = await api.post("/admin/shop/banners/weekly-refresh", { confirm: true });
            toast.success(`Retired ${res.data.retired_count} banner(s). Time to drop the new week!`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Weekly refresh failed");
        } finally { setBusy(null); }
    };

    const createDraft = async () => {
        if (!form.name.trim() || !form.prompt.trim()) {
            toast.error("Name and prompt are required");
            return;
        }
        setBusy("draft");
        try {
            await api.post("/admin/shop/banners/draft", {
                name: form.name.trim(),
                prompt: form.prompt.trim(),
                rarity: form.rarity,
                price: parseFloat(form.price) || 0,
                animation: form.animation,
                color: form.color,
            });
            toast.success("Draft generated — review below");
            setForm({ ...form, name: "", prompt: "" });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Draft generation failed");
        } finally { setBusy(null); }
    };

    const approveDraft = async (id) => {
        setBusy(id);
        try {
            await api.post(`/admin/shop/banners/drafts/${id}/approve`);
            toast.success("Banner published to shop \ud83d\ude80");
            await load();
        } catch (e) {
            toast.error("Approve failed");
        } finally { setBusy(null); }
    };

    const discardDraft = async (id) => {
        setBusy(id);
        try {
            await api.delete(`/admin/shop/banners/drafts/${id}`);
            toast.success("Draft discarded");
            await load();
        } catch (e) {
            toast.error("Failed");
        } finally { setBusy(null); }
    };

    const submitGift = async () => {
        const email = (giftEmail || "").trim();
        if (!email) { toast.error("Enter the recipient's email"); return; }
        if (!giftBanner) return;
        setGiftBusy(true);
        try {
            const res = await api.post("/admin/shop/banners/gift", {
                banner_id: giftBanner.id,
                email,
            });
            if (res.data.already_owned) {
                toast(`${email} already owns "${giftBanner.name}"`);
            } else {
                toast.success(`\u{1F381} Gifted "${giftBanner.name}" to ${email}`);
            }
            setGiftBanner(null);
            setGiftEmail("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Gift failed");
        } finally { setGiftBusy(false); }
    };

    return (
        <div className="space-y-8">
            {/* AI Generator panel */}
            <Card className="pp-bg-1 border-[#242436] text-white" data-testid="banner-generator-panel">
                <CardContent className="p-6 space-y-5">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md" style={{ background: "#1a0f2e", border: "1px solid #a855f755" }}>
                            <Wand2 className="w-4 h-4 text-[#a855f7]" />
                        </span>
                        <div>
                            <p className="font-display text-xl tracking-widest">AI BANNER GENERATOR</p>
                            <p className="text-xs text-[#8b8ba0]">Describe a new banner. Powered by Gemini Nano Banana. Drafts stay private until you approve them.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-[#b7b7c6]">Banner Name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Phoenix Flame" maxLength={80} className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="banner-gen-name" />
                        </div>
                        <div>
                            <Label className="text-[#b7b7c6]">Price ($)</Label>
                            <Input type="number" min={0} max={99.99} step={0.5} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="banner-gen-price" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[#b7b7c6]">AI Prompt</Label>
                        <Textarea rows={3} value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} placeholder="Wide 16:5 banner. Phoenix wreathed in golden flames against a dark night sky..." maxLength={600} className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="banner-gen-prompt" />
                        <p className="text-[10px] text-[#8b8ba0] mt-1 font-mono">{form.prompt.length}/600 — tip: include "Wide 16:5 banner" + visual style + color cues</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label className="text-[#b7b7c6]">Rarity</Label>
                            <Select value={form.rarity} onValueChange={(v) => setForm({ ...form, rarity: v })}>
                                <SelectTrigger className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="banner-gen-rarity"><SelectValue /></SelectTrigger>
                                <SelectContent className="pp-bg-0 border-[#242436] text-white">
                                    {RARITY_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value} className="text-white">{r.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[#b7b7c6]">Animation</Label>
                            <Select value={form.animation} onValueChange={(v) => setForm({ ...form, animation: v })}>
                                <SelectTrigger className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="banner-gen-animation"><SelectValue /></SelectTrigger>
                                <SelectContent className="pp-bg-0 border-[#242436] text-white">
                                    {ANIMATION_OPTIONS.map((a) => <SelectItem key={a.value} value={a.value} className="text-white">{a.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[#b7b7c6]">Accent Color</Label>
                            <div className="mt-1 flex items-center gap-2">
                                <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-14 h-10 p-1 pp-bg-0 border-[#242436]" data-testid="banner-gen-color" />
                                <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="flex-1 pp-bg-0 border-[#242436] text-white font-mono" />
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={createDraft} disabled={busy === "draft"} className="bg-[#a855f7] hover:brightness-110 text-white font-semibold" data-testid="banner-gen-submit">
                            {busy === "draft" ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4" /> Generate Draft</span>}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Drafts */}
            {drafts.length > 0 && (
                <div data-testid="banner-drafts-section">
                    <div className="mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#facc15]" />
                        <p className="font-display text-xl tracking-widest">PENDING DRAFTS ({drafts.length})</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {drafts.map((d) => {
                            const previewBanner = { image: d.image_base64, animation: d.animation, color: d.color, name: d.name };
                            const rarityColor = RARITY_META[d.rarity]?.color || "#a855f7";
                            return (
                                <Card key={d.id} className="pp-bg-1 border-[#242436] text-white" data-testid={`banner-draft-${d.id}`}>
                                    <CardContent className="p-4 space-y-3">
                                        <BannerDisplay banner={previewBanner} height={140} />
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="font-display text-lg tracking-widest text-white truncate">{d.name}</p>
                                                <p className="text-xs font-mono uppercase" style={{ color: rarityColor }}>{RARITY_META[d.rarity]?.label} · ${parseFloat(d.price).toFixed(2)}</p>
                                                <p className="text-xs text-[#8b8ba0] mt-1 line-clamp-2">{d.prompt}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button onClick={() => approveDraft(d.id)} disabled={busy === d.id} className="flex-1 bg-[#22c55e] hover:brightness-110 text-[#07130b] font-semibold" data-testid={`banner-draft-approve-${d.id}`}>
                                                {busy === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-1"><Check className="w-4 h-4" /> Publish</span>}
                                            </Button>
                                            <Button onClick={() => discardDraft(d.id)} disabled={busy === d.id} variant="secondary" className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]" data-testid={`banner-draft-discard-${d.id}`}>
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Live shop banners */}
            <div data-testid="banner-shop-section">
                <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                        <ShoppingBag className="w-4 h-4 text-[#facc15]" />
                        <p className="font-display text-xl tracking-widest">
                            SHOP CATALOG
                            <span className="ml-2 text-xs font-mono text-[#22c55e]">Live: {banners.filter(b => b.active).length}</span>
                            <span className="ml-2 text-xs font-mono text-[#8b8ba0]">Retired: {banners.filter(b => !b.active).length}</span>
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Button onClick={weeklyRefresh} disabled={busy === "weekly" || banners.filter(b => b.active).length === 0} variant="secondary" className="bg-[#1a0606] border border-[#ef4444]55 text-[#ef4444] hover:bg-[#2a0c0c] hover:text-white" data-testid="banner-weekly-refresh-button">
                            {busy === "weekly" ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> End The Week (Retire All)</span>}
                        </Button>
                        <Button onClick={seedMissing} disabled={busy === "seed"} variant="secondary" className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]" data-testid="banner-seed-images-button">
                            {busy === "seed" ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-2"><Wand2 className="w-4 h-4" /> Generate Missing Images</span>}
                        </Button>
                    </div>
                </div>
                <p className="text-xs text-[#8b8ba0] mb-4">Tip: Click <b>"End The Week"</b> to retire all live banners at once, then use the AI Generator above to publish 6 fresh banners for the new week. Owners keep their banners forever.</p>
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-56 rounded-xl pp-bg-1" />)}</div>
                ) : banners.length === 0 ? (
                    <p className="text-sm text-[#8b8ba0] py-8 text-center">No banners in the shop yet.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {banners.map((b) => {
                            const rarityColor = RARITY_META[b.rarity]?.color || "#a855f7";
                            const isRetired = b.active === false;
                            return (
                                <Card key={b.id} className={`pp-bg-1 border-[#242436] text-white ${isRetired ? "opacity-70" : ""}`} data-testid={`banner-shop-card-${b.slug}`}>
                                    <CardContent className="p-4 space-y-3">
                                        <div className="relative">
                                            <BannerDisplay banner={b} height={120} />
                                            {isRetired && (
                                                <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-widest bg-[#1f1a05] border border-[#facc15]55 text-[#facc15]">
                                                    Retired
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="font-display text-lg tracking-widest text-white truncate">{b.name}</p>
                                                <p className="text-xs font-mono uppercase" style={{ color: rarityColor }}>{RARITY_META[b.rarity]?.label} · ${b.price.toFixed(2)}</p>
                                                <p className="text-[10px] text-[#8b8ba0] mt-1 capitalize">{(b.animation || "").replaceAll("_", " ")}</p>
                                            </div>
                                            {!b.has_image && <span className="text-xs font-mono uppercase text-[#facc15]">No image</span>}
                                        </div>
                                        <div className="flex gap-2">
                                            <Button onClick={() => regenerate(b.slug)} disabled={busy === b.slug} variant="secondary" className="flex-1 bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]" data-testid={`banner-regenerate-${b.slug}`}>
                                                {busy === b.slug ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Regenerate</span>}
                                            </Button>
                                            <Button
                                                onClick={() => { setGiftBanner(b); setGiftEmail(""); }}
                                                variant="secondary"
                                                className="bg-[#1f0f2e] border border-[#a855f7]55 text-[#a855f7] hover:bg-[#2a0f3a] hover:text-white"
                                                data-testid={`banner-gift-${b.slug}`}
                                                title="Gift this banner to a user"
                                            >
                                                <span className="inline-flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Gift</span>
                                            </Button>
                                            <Button
                                                onClick={() => toggleActive(b)}
                                                disabled={busy === b.id}
                                                variant="secondary"
                                                className={isRetired
                                                    ? "bg-[#07130b] border border-[#22c55e]55 text-[#22c55e] hover:bg-[#0a1f0e]"
                                                    : "bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]"}
                                                data-testid={`banner-toggle-${b.slug}`}
                                                title={isRetired ? "Bring back to shop" : "Retire from shop"}
                                            >
                                                {busy === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                                    isRetired
                                                        ? <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Bring Back</span>
                                                        : <span className="inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Retire</span>
                                                )}
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Gift Banner Dialog */}
            <Dialog open={!!giftBanner} onOpenChange={(open) => { if (!open) { setGiftBanner(null); setGiftEmail(""); } }}>
                <DialogContent className="pp-bg-1 border-[#242436] text-white" data-testid="banner-gift-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display tracking-widest inline-flex items-center gap-2">
                            <Gift className="w-5 h-5 text-[#a855f7]" /> GIFT BANNER
                        </DialogTitle>
                        <DialogDescription className="text-[#b7b7c6]">
                            Give "<span className="text-white font-semibold">{giftBanner?.name}</span>" to any user. It lands in their Vault instantly and they can equip it on their profile.
                        </DialogDescription>
                    </DialogHeader>
                    {giftBanner && (
                        <div className="space-y-4">
                            <BannerDisplay banner={giftBanner} height={110} />
                            <div>
                                <Label className="text-[#b7b7c6]">Recipient Email</Label>
                                <Input
                                    value={giftEmail}
                                    onChange={(e) => setGiftEmail(e.target.value)}
                                    placeholder="user@example.com"
                                    type="email"
                                    autoFocus
                                    className="mt-1 pp-bg-0 border-[#242436] text-white"
                                    data-testid="banner-gift-email"
                                    onKeyDown={(e) => { if (e.key === "Enter" && !giftBusy) submitGift(); }}
                                />
                                <p className="text-[10px] text-[#8b8ba0] mt-1 font-mono">User must already have an account with this email.</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            variant="secondary"
                            onClick={() => { setGiftBanner(null); setGiftEmail(""); }}
                            disabled={giftBusy}
                            className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]"
                            data-testid="banner-gift-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitGift}
                            disabled={giftBusy || !giftEmail.trim()}
                            className="bg-[#a855f7] hover:brightness-110 text-white font-semibold"
                            data-testid="banner-gift-submit"
                        >
                            {giftBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-1"><Gift className="w-4 h-4" /> Send Gift</span>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ----------- Support Ratings Tab (admin-only) -----------
function SupportRatingsManager() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({ total: 0, average: 0, counts: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/support/ratings");
            setItems(res.data.items || []);
            setStats({ total: res.data.total || 0, average: res.data.average || 0, counts: res.data.counts || {} });
        } catch (e) { toast.error("Failed to load ratings"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="grid gap-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-xl pp-bg-1" />)}</div>;

    const formatDate = (iso) => {
        if (!iso) return "—";
        try { return new Date(iso).toLocaleDateString() + " " + new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
    };

    return (
        <div className="space-y-6">
            {/* Stats overview */}
            <Card className="pp-bg-1 border-[#242436] text-white" data-testid="support-ratings-stats">
                <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md" style={{ background: "#1f1a05", border: "1px solid #facc1555" }}>
                            <Star className="w-4 h-4 text-[#facc15]" />
                        </span>
                        <div>
                            <p className="font-display text-xl tracking-widest">SUPPORT FEEDBACK</p>
                            <p className="text-xs text-[#8b8ba0]">Private ratings and descriptions only you can see.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="pp-bg-0 rounded-md p-3 border border-[#242436]">
                            <p className="text-[10px] font-mono uppercase text-[#8b8ba0]">Total Reviews</p>
                            <p className="mt-1 font-display text-2xl tracking-widest text-white" data-testid="support-ratings-total">{stats.total}</p>
                        </div>
                        <div className="pp-bg-0 rounded-md p-3 border border-[#242436]">
                            <p className="text-[10px] font-mono uppercase text-[#8b8ba0]">Average</p>
                            <p className="mt-1 font-display text-2xl tracking-widest text-[#facc15] inline-flex items-center gap-1" data-testid="support-ratings-average">
                                {stats.average} <Star className="w-4 h-4" style={{ fill: "#facc15", color: "#facc15" }} />
                            </p>
                        </div>
                        <div className="pp-bg-0 rounded-md p-3 border border-[#242436] col-span-2">
                            <p className="text-[10px] font-mono uppercase text-[#8b8ba0] mb-1">Distribution</p>
                            <div className="space-y-0.5">
                                {[5, 4, 3, 2, 1].map((n) => {
                                    const count = stats.counts?.[n] || 0;
                                    const pct = stats.total ? (count / stats.total) * 100 : 0;
                                    return (
                                        <div key={n} className="flex items-center gap-2 text-xs">
                                            <span className="w-3 text-[#facc15] font-mono">{n}</span>
                                            <Star className="w-3 h-3" style={{ fill: "#facc15", color: "#facc15" }} />
                                            <div className="flex-1 h-1.5 bg-[#242436] rounded overflow-hidden">
                                                <div className="h-full bg-[#facc15]" style={{ width: `${pct}%` }} />
                                            </div>
                                            <span className="text-[#b7b7c6] font-mono w-6 text-right">{count}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Reviews list */}
            {items.length === 0 ? (
                <p className="text-sm text-[#8b8ba0] py-8 text-center" data-testid="support-ratings-empty">No support ratings yet. Ratings appear here after support staff end conversations and users submit feedback.</p>
            ) : (
                <div className="space-y-3" data-testid="support-ratings-list">
                    {items.map((r) => {
                        const ratingColor = r.rating >= 4 ? "#22c55e" : r.rating === 3 ? "#facc15" : "#ef4444";
                        return (
                            <Card key={r.conversation_id} className="pp-bg-1 border-[#242436] text-white" data-testid={`support-rating-${r.conversation_id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-3 flex-wrap">
                                        {/* Avatar of user */}
                                        <span className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white flex-shrink-0" style={{ background: "#a855f7" }}>
                                            {(r.user?.display_name || "?").charAt(0).toUpperCase()}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-semibold text-white truncate">{r.user?.display_name || r.rated_by_display_name || "User"}</p>
                                                <RoleBadge role={r.user?.role} />
                                                <span className="inline-flex items-center gap-0.5 ml-1">
                                                    {[1, 2, 3, 4, 5].map((n) => (
                                                        <Star key={n} className="w-3.5 h-3.5" style={{ fill: n <= r.rating ? ratingColor : "transparent", color: n <= r.rating ? ratingColor : "#4a4a5e" }} />
                                                    ))}
                                                </span>
                                                <span className="text-xs font-mono uppercase ml-1" style={{ color: ratingColor }}>{r.rating}/5</span>
                                            </div>
                                            <p className="text-xs text-[#8b8ba0] mt-0.5 font-mono">
                                                Support: <span className="text-[#22d3ee]">{r.support?.display_name || r.closed_by_display_name || "—"}</span>
                                                {r.subject ? ` · ${r.subject}` : ""}
                                                {" · "}
                                                Rated {formatDate(r.rated_at)}
                                            </p>
                                            {r.description && (
                                                <p className="mt-2 text-sm text-white whitespace-pre-wrap pp-bg-0 rounded-md p-3 border border-[#242436]" data-testid={`support-rating-description-${r.conversation_id}`}>
                                                    "{r.description}"
                                                </p>
                                            )}
                                            <div className="mt-2 flex gap-2 flex-wrap">
                                                <Button onClick={() => nav(`/messages/${r.conversation_id}`)} variant="secondary" className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]" data-testid={`support-rating-open-${r.conversation_id}`}>
                                                    <MessageSquare className="w-3.5 h-3.5 mr-1" /> Open Convo
                                                </Button>
                                                {r.user && (
                                                    <Button onClick={() => nav(`/u/${r.user.id}`)} variant="secondary" className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]">
                                                        <UserCircle className="w-3.5 h-3.5 mr-1" /> User Profile
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ----------- Support Reports Manager Tab -----------
function ReportsManager() {
    const [items, setItems] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [actionDialog, setActionDialog] = useState(null); // { report, action } or null
    const [actionNote, setActionNote] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/reports", { params: { status: filter } });
            setItems(res.data.items || []);
            setPendingCount(res.data.pending_count || 0);
        } catch (e) { toast.error("Failed to load reports"); }
        finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const resolve = async (report, action, note) => {
        setBusy(report.id);
        try {
            await api.post(`/admin/reports/${report.id}/resolve`, { action, note: note || undefined });
            toast.success(`Action: ${action.replaceAll("_", " ")}`);
            setActionDialog(null);
            setActionNote("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Resolve failed");
        } finally { setBusy(null); }
    };

    const formatDate = (iso) => {
        if (!iso) return "—";
        try { return new Date(iso).toLocaleDateString() + " " + new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
    };

    return (
        <div className="space-y-4">
            <Card className="pp-bg-1 border-[#242436] text-white" data-testid="reports-header">
                <CardContent className="p-5 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md" style={{ background: "#1a0606", border: "1px solid #ef444455" }}>
                            <Flag className="w-4 h-4 text-[#ef4444]" />
                        </span>
                        <div>
                            <p className="font-display text-xl tracking-widest">REPORTS QUEUE</p>
                            <p className="text-xs text-[#8b8ba0]">Reports filed by users. Resolve them to keep the community clean.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-mono uppercase text-[#facc15]">Pending: {pendingCount}</span>
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="w-32 pp-bg-0 border-[#242436] text-white" data-testid="reports-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="pp-bg-0 border-[#242436] text-white">
                                <SelectItem value="pending" className="text-white">Pending</SelectItem>
                                <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
                                <SelectItem value="all" className="text-white">All</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {loading ? (
                <div className="grid gap-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-xl pp-bg-1" />)}</div>
            ) : items.length === 0 ? (
                <p className="text-sm text-[#8b8ba0] py-8 text-center" data-testid="reports-empty">No {filter !== "all" ? filter : ""} reports.</p>
            ) : (
                <div className="space-y-3" data-testid="reports-list">
                    {items.map((r) => {
                        const isPending = r.status === "pending";
                        return (
                            <Card key={r.id} className="pp-bg-1 border-[#242436] text-white" data-testid={`report-${r.id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Flag className="w-4 h-4 text-[#ef4444]" />
                                                <p className="font-semibold text-white">
                                                    Report on{" "}
                                                    <span className="text-[#facc15]">{r.target_user_name || "Unknown user"}</span>
                                                </p>
                                                {r.channel && <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-[#14141c] border border-[#242436]">{r.channel}</span>}
                                                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${isPending ? "bg-[#1f1a05] text-[#facc15] border border-[#facc15]66" : "bg-[#07130b] text-[#22c55e] border border-[#22c55e]66"}`}>
                                                    {r.status}
                                                </span>
                                            </div>
                                            <p className="text-xs text-[#8b8ba0] mt-1 font-mono">
                                                Reported by <span className="text-[#22d3ee]">{r.reporter_name}</span> · {formatDate(r.created_at)}
                                            </p>
                                            <p className="mt-2 text-sm text-white pp-bg-0 rounded-md p-3 border border-[#242436]">
                                                <span className="text-[10px] font-mono uppercase text-[#8b8ba0]">Reason:</span><br />
                                                "{r.reason}"
                                            </p>
                                            {r.content_snapshot && (
                                                <div className="mt-2 text-sm pp-bg-0 rounded-md p-3 border border-[#ef444444]">
                                                    <p className="text-[10px] font-mono uppercase text-[#ef4444]">Reported message:</p>
                                                    <p className="mt-1 text-[#b7b7c6]">"{r.content_snapshot}"</p>
                                                </div>
                                            )}
                                            {!isPending && (
                                                <p className="mt-2 text-xs font-mono text-[#22c55e]">
                                                    ✓ Resolved by {r.resolved_by_name} · Action: <b>{(r.action_taken || "").replaceAll("_", " ")}</b>
                                                    {r.resolve_note ? ` · "${r.resolve_note}"` : ""}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {isPending && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <Button onClick={() => resolve(r, "dismiss")} disabled={busy === r.id} variant="secondary" className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]" data-testid={`report-dismiss-${r.id}`}>
                                                <X className="w-3.5 h-3.5 mr-1" /> Dismiss
                                            </Button>
                                            {r.target_message_id && (
                                                <Button onClick={() => resolve(r, "delete_message")} disabled={busy === r.id} variant="secondary" className="bg-[#1a0606] border border-[#ef4444]55 text-[#ef4444] hover:bg-[#2a0c0c]" data-testid={`report-delete-msg-${r.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete msg
                                                </Button>
                                            )}
                                            <Button onClick={() => { setActionDialog({ report: r, action: "warn" }); setActionNote(r.reason || ""); }} disabled={busy === r.id} variant="secondary" className="bg-[#1f1a05] border border-[#facc15]55 text-[#facc15] hover:bg-[#2a2208]" data-testid={`report-warn-${r.id}`}>
                                                <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Warn user
                                            </Button>
                                            <Button onClick={() => { setActionDialog({ report: r, action: "ban_24h" }); setActionNote(r.reason || ""); }} disabled={busy === r.id} variant="secondary" className="bg-[#1a0606] border border-[#ef4444]55 text-[#ef4444] hover:bg-[#2a0c0c]" data-testid={`report-ban-24h-${r.id}`}>
                                                <Ban className="w-3.5 h-3.5 mr-1" /> Ban 24h
                                            </Button>
                                            <Button onClick={() => { setActionDialog({ report: r, action: "ban_7d" }); setActionNote(r.reason || ""); }} disabled={busy === r.id} variant="secondary" className="bg-[#1a0606] border border-[#ef4444]55 text-[#ef4444] hover:bg-[#2a0c0c]" data-testid={`report-ban-7d-${r.id}`}>
                                                <Ban className="w-3.5 h-3.5 mr-1" /> Ban 7d
                                            </Button>
                                            <Button onClick={() => { setActionDialog({ report: r, action: "ban_perm" }); setActionNote(r.reason || ""); }} disabled={busy === r.id} variant="secondary" className="bg-[#1a0606] border border-[#ef4444]55 text-[#ef4444] hover:bg-[#2a0c0c]" data-testid={`report-ban-perm-${r.id}`}>
                                                <Ban className="w-3.5 h-3.5 mr-1" /> Perm ban
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Confirm action dialog */}
            <Dialog open={!!actionDialog} onOpenChange={(o) => { if (!o) { setActionDialog(null); setActionNote(""); } }}>
                <DialogContent className="pp-bg-1 border-[#242436] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            {actionDialog?.action === "warn" ? "Warn user" : `Ban user (${actionDialog?.action?.replace("ban_", "")})`}
                        </DialogTitle>
                        <DialogDescription className="text-[#b7b7c6]">
                            User: <span className="text-[#facc15]">{actionDialog?.report?.target_user_name}</span>
                            {actionDialog?.action === "ban_perm" && <><br /><span className="text-[#ef4444] text-xs">⚠ Permanent ban — they can't post in chat or DMs ever again until you unban them.</span></>}
                            {actionDialog?.action === "warn" && <><br /><span className="text-[10px] text-[#facc15]">Auto-ban triggers at 3 warnings.</span></>}
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        value={actionNote}
                        onChange={(e) => setActionNote(e.target.value.slice(0, 300))}
                        placeholder="Note (will be visible to the user as the reason)"
                        rows={3}
                        className="pp-bg-0 border-[#242436] text-white"
                    />
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setActionDialog(null)} className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]">Cancel</Button>
                        <Button onClick={() => actionDialog && resolve(actionDialog.report, actionDialog.action, actionNote)} disabled={!actionNote.trim() || busy} className="bg-[#ef4444] hover:brightness-110 text-white font-semibold" data-testid="report-action-confirm">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ----------- Owner page wrapper -----------
export default function Owner() {
    const { user, loading: authLoading, refresh } = useAuth();
    const nav = useNavigate();
    const [retried, setRetried] = useState(false);

    // Best-effort retry refresh on mount in case AuthProvider's initial fetch had a transient issue
    useEffect(() => {
        if (!authLoading && !user && !retried) {
            const token = typeof window !== "undefined" && localStorage.getItem("token");
            if (token) {
                setRetried(true);
                refresh();
            }
        }
    }, [authLoading, user, retried, refresh]);

    // Loading state
    if (authLoading) {
        return (
            <div className="min-h-screen pp-bg-0 text-white">
                <Nav />
                <section className="py-16 max-w-6xl mx-auto px-4 grid gap-4">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-xl pp-bg-1" />)}
                </section>
                <Footer />
            </div>
        );
    }

    // Not logged in: show clear "login required" instead of redirect (avoid race conditions)
    if (!user) {
        const tokenInStorage = typeof window !== "undefined" && localStorage.getItem("token");
        if (tokenInStorage && !retried) {
            // Brief grace period — auth still resolving
            return (
                <div className="min-h-screen pp-bg-0 text-white">
                    <Nav />
                    <section className="py-16 max-w-6xl mx-auto px-4 grid gap-4">
                        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-xl pp-bg-1" />)}
                    </section>
                    <Footer />
                </div>
            );
        }
        return (
            <div className="min-h-screen pp-bg-0 text-white">
                <Nav />
                <section className="py-20 max-w-md mx-auto px-4 text-center">
                    <UserCircle className="w-12 h-12 mx-auto text-[#8b8ba0]" />
                    <h1 className="mt-4 font-display text-2xl tracking-widest">LOGIN REQUIRED</h1>
                    <p className="mt-1 text-sm text-[#b7b7c6]">The Owner Dashboard is for staff only. Log in to continue.</p>
                    <Button onClick={() => nav("/login?next=/owner")} className="mt-5 bg-[#6f3fec] hover:brightness-110 text-white" data-testid="owner-login-required-cta">Log in</Button>
                </section>
                <Footer />
            </div>
        );
    }

    // Logged in but not staff
    if (user.role !== "admin" && user.role !== "support") {
        return (
            <div className="min-h-screen pp-bg-0 text-white">
                <Nav />
                <section className="py-20 max-w-md mx-auto px-4 text-center">
                    <ShieldCheck className="w-12 h-12 mx-auto text-[#8b8ba0]" />
                    <h1 className="mt-4 font-display text-2xl tracking-widest">STAFF ONLY</h1>
                    <p className="mt-1 text-sm text-[#b7b7c6]">This dashboard is for Owner & Support team. Reach out via the Help button if you need a hand.</p>
                    <Button onClick={() => nav("/")} className="mt-5 bg-[#14141c] border border-[#242436] text-white">Back to Picks</Button>
                </section>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-10 sm:py-14">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center gap-3 mb-6 flex-wrap">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-md" style={{ background: user.role === "admin" ? "#1f1a05" : "#06181f", border: `1px solid ${user.role === "admin" ? "#facc15" : "#22d3ee"}55` }}>
                            {user.role === "admin" ? <Crown className="w-5 h-5 text-[#facc15]" /> : <ShieldCheck className="w-5 h-5 text-[#22d3ee]" />}
                        </span>
                        <div>
                            <p className="text-xs font-mono uppercase tracking-widest" style={{ color: user.role === "admin" ? "#facc15" : "#22d3ee" }}>
                                {user.role === "admin" ? "Owner Dashboard" : "Staff Dashboard"}
                            </p>
                            <h1 className="font-display text-3xl sm:text-4xl tracking-widest">CONTROL PANEL</h1>
                        </div>
                    </div>

                    <Tabs defaultValue="picks" className="w-full">
                        <TabsList className="pp-bg-1 border border-[#242436]">
                            <TabsTrigger value="picks" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="owner-tab-picks">
                                <ImageIcon className="w-4 h-4 mr-1" /> Picks
                            </TabsTrigger>
                            <TabsTrigger value="help" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="owner-tab-help">
                                <MessageCircleQuestion className="w-4 h-4 mr-1" /> Help Queue
                            </TabsTrigger>
                            {user.role === "admin" && (
                                <TabsTrigger value="flow" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="owner-tab-flow">
                                    <Flame className="w-4 h-4 mr-1" /> Flow Meter
                                </TabsTrigger>
                            )}
                            {user.role === "admin" && (
                                <TabsTrigger value="shop" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="owner-tab-shop">
                                    <ShoppingBag className="w-4 h-4 mr-1" /> Banner Shop
                                </TabsTrigger>
                            )}
                            {user.role === "admin" && (
                                <TabsTrigger value="giveaways" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="owner-tab-giveaways">
                                    <Trophy className="w-4 h-4 mr-1" /> Giveaways
                                </TabsTrigger>
                            )}
                            {user.role === "admin" && (
                                <TabsTrigger value="ratings" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="owner-tab-ratings">
                                    <Star className="w-4 h-4 mr-1" /> Ratings
                                </TabsTrigger>
                            )}
                            {(user.role === "admin" || user.role === "support") && (
                                <TabsTrigger value="reports" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="owner-tab-reports">
                                    <Flag className="w-4 h-4 mr-1" /> Reports
                                </TabsTrigger>
                            )}
                            {user.role === "admin" && (
                                <TabsTrigger value="users" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="owner-tab-users">
                                    <Users className="w-4 h-4 mr-1" /> Users
                                </TabsTrigger>
                            )}
                        </TabsList>
                        <TabsContent value="picks" className="mt-6">
                            <PicksManager />
                        </TabsContent>
                        <TabsContent value="help" className="mt-6">
                            <HelpManager />
                        </TabsContent>
                        {user.role === "admin" && (
                            <TabsContent value="flow" className="mt-6">
                                <FlowMeterManager />
                            </TabsContent>
                        )}
                        {user.role === "admin" && (
                            <TabsContent value="shop" className="mt-6">
                                <BannerShopManager />
                            </TabsContent>
                        )}
                        {user.role === "admin" && (
                            <TabsContent value="giveaways" className="mt-6">
                                <GiveawayManager />
                            </TabsContent>
                        )}
                        {user.role === "admin" && (
                            <TabsContent value="ratings" className="mt-6">
                                <SupportRatingsManager />
                            </TabsContent>
                        )}
                        {(user.role === "admin" || user.role === "support") && (
                            <TabsContent value="reports" className="mt-6">
                                <ReportsManager />
                            </TabsContent>
                        )}
                        {user.role === "admin" && (
                            <TabsContent value="users" className="mt-6">
                                <UsersManager />
                            </TabsContent>
                        )}
                    </Tabs>
                </div>
            </section>
            <Footer />
        </div>
    );
}
